import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames/bind';

import { restProps } from '../../utils';
import Type from '../../Typography';
import Icon from '../../Icon';

import styles from './Basic.less';

const cx = classnames.bind(styles);

const CACHE = new WeakMap();

/**
 * **Access:**
 * ```js
 * import {Columns} from '@iss/client-react-components';
 *
 * const column1 = Columns.Basic.forField({
 * 	field: 'fieldName',
 * 	field: 'Displayed Column Label',
 * 	width: 100
 * });
 *
 * <Table columns={[ column1, ... ]} />
 * ```
 *
 * A basic column & factory. Used for *most* columns.
 *
 * @type {Object}
 */
export default class BasicColumn extends React.PureComponent {
  static propTypes = {
    item: PropTypes.any,
    alternate: PropTypes.bool,
    condensed: PropTypes.bool,
    selected: PropTypes.bool,
    onSelect: PropTypes.func,

    getCurrentSort: PropTypes.func,
    onChangeSort: PropTypes.func,
  };

  static Header = class extends React.Component {
    static rendersContainer = true;
    render() {
      const {
        props: { className, thClassName, getCurrentSort, onChangeSort },
        sortable,
        sortOn,
        minWidth,
        width,
      } = this;

      const Wrapper = sortable ? 'div' : React.Fragment;
      const style = {
        ...(!width ? null : { width, minWidth: width }),
        ...(!minWidth ? null : { minWidth }),
      };

      const sort = sortable && (() => onChangeSort(sortOn));
      const { sorted, order } = sortable && getCurrentSort ? getCurrentSort(sortOn) : {};
      const reversed = sorted && order === 'desc';

      return (
        <th
          className={cx(thClassName, 'cell', { sortable, sorted, reversed })}
          style={style}
          onClick={onChangeSort && sort}
        >
          <Wrapper>
            <Type face="subtext1" as="span" className={className}>
              {this.columnLabel}
            </Type>
            {sortable && <Icon className={cx('chevron')} name="icon-chevron-down" />}
          </Wrapper>
        </th>
      );
    }
  };

  /**
   * Column Factory. Use this to build a column definition for a particular property.
   *
   * @public
   * @param  {object} options       Column options.
   * @param  {string|function} options.field       The property on the row to source this column cell from. Or a function that can return a string.
   * @param  {string} options.columnLabel The value to put in the header cell.
   * @param  {number} options.minWidth    The minimum width of this column (in logical pixels)
   * @param  {number} options.width       The fixed width of this column (in logical pixels)
   * @return {Column}
   */
  static forField(options) {
    if (typeof options !== 'object') {
      options = {
        field: options,
      };
    }

    const Column = this;
    const { field, columnLabel, width, minWidth, align, sortable, sortOn, ...config } = options;
    const key = field && `${Column.name}-${JSON.stringify(options)}`;

    // WeakMap's cannot store primitive keys, so when field is a function, use the WeakMap,
    // otherwise use hardrefs on the object as a property key.
    const cached = CACHE.get(field) || CACHE[key];
    if (cached) {
      return cached;
    }

    const cmp = class extends Column {
      align = align;
      config = config;
      field = field;
      static HeaderComponent = class extends Column.Header {
        columnLabel = columnLabel;
        field = field;
        sortable = sortable;
        sortOn = sortOn || field;
        minWidth = minWidth;
        width = width;
      };
    };

    if (field) {
      if (typeof field === 'function') {
        CACHE.set(field, cmp);
      } else {
        CACHE[key] = cmp;
      }
    }

    return cmp;
  }

  get value() {
    const {
      field,
      props: { item },
    } = this;

    try {
      if (typeof field === 'function') {
        return field(item);
      } else if (typeof field === 'string') {
        //attempt to traverse a dot-notated property path
        return field.split('.').reduce((a, x) => a[x], item);
      }
    } catch {
      //let it be blank
    }

    return (item && item[field]) || '';
  }

  render() {
    const { align, value, config } = this;
    const { as } = config;

    return !value ? null : React.isValidElement(value) || as === React.Fragment ? (
      <>{value}</>
    ) : (
      <Type
        {...restProps(BasicColumn, this.props)}
        className={cx('cell', { [align]: /left|right|center/.test(align) })}
        title={value}
        as={as || 'span'}
      >
        {value}
      </Type>
    );
  }
}
